import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { megaMenuGroups } from "@layouts/constants";
import { MegaMenuItem } from "@layouts/types";
import styled, { css, keyframes } from "styled-components";

// ========================================
// Animations
// ========================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

// ========================================
// Styled Components
// ========================================

/**
 * The nav row's height, and the only thing the mega menu may hang from.
 *
 * `top: 100%` used to be used here, which resolves against SecondHeaderContainer
 * — and the global banners (incident, pending-email, beta, past-due, cancelled)
 * render INSIDE that same container, after this component. So the container's
 * height was the nav PLUS every banner showing, and the menu opened below them
 * instead of below the nav: with the blue banner up, it dropped a banner's
 * height too far and left the banner uncovered above it.
 *
 * 72px is the nav row itself. It is not a new number — SecondHeader's background
 * gradient already hardcodes the same boundary (`#59412e 0, #59412e 72px,
 * transparent 72px`) for exactly this reason, and the nav Grid carries
 * `minHeight="72px"`. Change one and change the other.
 */
const NAV_H = "72px";

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: ${NAV_H};
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 49;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  transition: opacity 0.35s ease;
`;

const PanelWrapper = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(${NAV_H} - 2px);
  left: 0;
  width: 100%;
  /* Above the banners deliberately. They sit later in the DOM but carry no
     z-index, so a positioned element with one wins regardless of order — that
     is what lets the panel cover a banner rather than be painted over by it. */
  z-index: 99;
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
`;

const PanelBg = styled.div<{ $isOpen: boolean }>`
  background: #fff0e2;
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? "300px" : "0")};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease;
`;

const PointerTip = styled.div<{ $isOpen: boolean; $x: number }>`
  position: absolute;
  top: -14px;
  left: ${({ $x }) => $x}px;
  transform: translateX(-50%);
  width: 56px;
  height: 28px;
  background: #fff0e2;
  clip-path: path("M28 0 C28 28, 0 0, 0 28 L56 28 C56 0, 28 28, 28 0Z");
  overflow: hidden;
  clip: rect(0, 56px, 14px, 0);
  z-index: 100;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const MegaInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 28px 40px 32px;
  display: flex;
  gap: 16px;
`;

const MegaCard = styled.div<{ $delay: number; $isHighlighted?: boolean }>`
  flex: 1;
  background: ${({ $isHighlighted }) => ($isHighlighted ? "#ddedf8" : "#59412e")};
  text-decoration: none;
  border: ${({ $isHighlighted }) => ($isHighlighted ? "1px solid #8ab8d8" : "none")};
  border-radius: 12px;
  padding: 28px 24px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  position: relative;
  box-shadow: 0 4px 16px rgba(40, 25, 12, 0.2);
  opacity: 0;
  transform: translateY(12px) scale(0.98);
  transition: background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;

  /* One rule for the card's whole life. There used to be a second "revealed"
     rule that took over on a timer once the entrance finished — and swapping
     between the two is what made the cards twitch a pixel as they landed. An
     element with a running or filled transform animation gets its own
     compositing layer; take the animation away and the layer goes with it, so
     the text re-rasterises against the device pixel grid and everything inside
     the card shifts. Matching the two states' computed transforms did not help,
     because the layer, not the value, is what moves the glyphs.
     Nothing needs the swap: the panel unmounts its cards when it closes and
     re-keys them when the group changes, so every open mounts them fresh and
     the animation runs from the start on its own. The forwards fill mode holds
     the end state for as long as they live. */
  animation: ${fadeIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${({ $delay }) => $delay}s forwards;

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      h4 { color: #2a5a7a; }
      p { color: rgba(42, 90, 122, 0.65); }
      & > div { background: rgba(42, 122, 181, 0.12); }
      & > div svg { stroke: #2a7ab5; }
    `}

  &:hover {
    background: ${({ $isHighlighted }) => ($isHighlighted ? "#d0e8f8" : "#654a35")};
    border-color: ${({ $isHighlighted }) => ($isHighlighted ? "#9cc8e8" : "transparent")};
    box-shadow: 0 10px 36px rgba(40, 25, 12, 0.3);

    ${({ $isHighlighted }) =>
      $isHighlighted &&
      css`
        h4 { color: #1a4a6a !important; }
        p { color: rgba(42, 90, 122, 0.8) !important; }
        & > div { background: rgba(42, 122, 181, 0.18) !important; }
        & > div svg { stroke: #1a6aaa !important; }
      `}
  }
`;

const CardIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255, 220, 168, 0.12);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  transition: background 0.3s ease;

  svg {
    width: 24px;
    height: 24px;
    fill: none;
    stroke: #9cd3fd;
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: stroke 0.3s ease;
  }

  ${MegaCard}:hover & {
    background: rgba(255, 220, 168, 0.25);
  }

  ${MegaCard}:hover & svg {
    stroke: #b8e2ff;
  }
`;

const CardTitle = styled.h4`
  font-family: "Raleway", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
  letter-spacing: 0.2px;
  margin: 0;
  transition: color 0.25s ease;

  ${MegaCard}:hover & {
    color: #ffdca8;
  }
`;

const CardDescription = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
  font-weight: 400;
  margin: 0;
`;

const TestBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 2px 10px;
  border-radius: 10px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: #9cd3fd;
  color: #fff;
  border: none;
  line-height: 18px;
`;

// ========================================
// Component
// ========================================

type MegaMenuProps = {
  activeGroupKey: string | null;
  onClose: () => void;
  onHide: () => void;
  navItemRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
};

const MegaMenu: FC<MegaMenuProps> = ({ activeGroupKey, onClose, onHide, navItemRefs }) => {
  const navigate = useNavigate();
  const [pointerX, setPointerX] = useState(0);

  const activeGroup = megaMenuGroups.find((g) => g.key === activeGroupKey) || null;
  const isOpen = !!activeGroup;

  // Update pointer position
  const updatePointerPosition = useCallback(() => {
    if (!activeGroupKey || !navItemRefs.current[activeGroupKey]) return;
    const el = navItemRefs.current[activeGroupKey];
    if (el) {
      const rect = el.getBoundingClientRect();
      setPointerX(rect.left + rect.width / 2);
    }
  }, [activeGroupKey, navItemRefs]);

  useEffect(() => {
    updatePointerPosition();
  }, [activeGroupKey, updatePointerPosition]);

  // Handle resize — hide on mobile but don't forget
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        onHide();
        return;
      }
      updatePointerPosition();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [onHide, updatePointerPosition]);

  // Freeze the page while the panel is open. The overlay is absolutely
  // positioned, so it darkens the page without stopping the wheel from
  // scrolling straight past it. Desktop only — below 1024 this menu is closed
  // and the mobile drawer handles itself.
  //
  // Refusing the scroll events rather than hiding the overflow. `overflow:
  // hidden` takes the scrollbar away, which widens the viewport and slides
  // every centred thing sideways; padding the body back by that width stops the
  // slide but leaves a flat strip down the right where the scrollbar was —
  // against full-bleed section backgrounds it reads as a stray bar. This way
  // the page does not move at all and the scrollbar stays exactly where it was.
  useEffect(() => {
    if (!isOpen || window.innerWidth <= 1024) return;

    const refuse = (e: Event) => e.preventDefault();
    const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Spacebar"]);
    const refuseKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Never swallow a keystroke someone is typing.
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      if (SCROLL_KEYS.has(e.key)) e.preventDefault();
    };

    // passive: false — the default for wheel/touchmove on window is passive,
    // where preventDefault is ignored.
    window.addEventListener("wheel", refuse, { passive: false });
    window.addEventListener("touchmove", refuse, { passive: false });
    window.addEventListener("keydown", refuseKey);

    return () => {
      window.removeEventListener("wheel", refuse);
      window.removeEventListener("touchmove", refuse);
      window.removeEventListener("keydown", refuseKey);
    };
  }, [isOpen]);

  const handleCardClick = (item: MegaMenuItem) => {
    onClose();
    navigate(item.route);
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <PanelWrapper $isOpen={isOpen}>
        <PointerTip $isOpen={isOpen} $x={pointerX} />
        <PanelBg $isOpen={isOpen}>
          {activeGroup && (
            <MegaInner onClick={(e) => e.stopPropagation()}>
              {activeGroup.items.map((item, i) => (
                <MegaCard
                  as={Link}
                  to={item.route}
                  key={`${activeGroup.key}-${item.route}-${i}`}
                  $delay={0.1 + i * 0.08}
                  $isHighlighted={item.isHighlighted}
                  onClick={(e: React.MouseEvent) => {
                    // Allow middle-click / ctrl-click to open in new tab naturally
                    if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                      e.preventDefault();
                      handleCardClick(item);
                    }
                  }}
                >
                  {item.badge && <TestBadge>{item.badge}</TestBadge>}
                  <CardIcon>
                    <svg
                      viewBox="0 0 24 24"
                      style={item.iconTransform ? { transform: item.iconTransform } : undefined}
                    >
                      <path d={item.iconPath} />
                    </svg>
                  </CardIcon>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                </MegaCard>
              ))}
            </MegaInner>
          )}
        </PanelBg>
      </PanelWrapper>
    </>
  );
};

export default MegaMenu;
